<script setup lang="ts">
import { computed, ref, type PropType } from 'vue';
import type { SidebarLinkModel } from './interfaces/SidebarLinkModel';
import { Popup, UserAvatar } from '../common/';
import UserOptions from './UserOptions.vue';
import { SidebarLinkIdentifiers } from './LinkIdentifiers';
import { useRoute } from 'vue-router';
import { LocalStorageUtils, routeConstants } from '@/utils';

const props = defineProps({
  link: {
    type: Object as PropType<SidebarLinkModel>,
    required: true
  },
  showProfileOptions: {
    type: Boolean,
    required: false,
    default: false
  }
});

const route = useRoute();

const isLinkActive = computed(() => {
  return route.path.includes(props.link.route) || 
         (props.link.id === SidebarLinkIdentifiers.DATA && route.name === routeConstants.ARCHIVE.name);
});

const reference = ref<HTMLElement | null>(null);
const popupReference = ref<HTMLElement | undefined>();

const emit = defineEmits(['linkClick', 'outsidePopupClick']);

const xValue = ref('0px');
const yValue = ref('0px');

const linkClick = (ev: MouseEvent) => {
  if (props.link.id === SidebarLinkIdentifiers.USER_PROFILE) {
    const bounds = reference.value?.getBoundingClientRect();
    if (bounds) {
      setTimeout(() => {
        xValue.value = `${bounds.x + bounds.width}px`;
        // @ts-expect-error: target could be null in the following statement
        const popupBounds = popupReference.value?.target?.getBoundingClientRect();
        if (popupBounds) {
          yValue.value = `${ev.clientY - popupBounds.height}px`;
        }
      }, 10);
    }
  }
  emit('linkClick', props.link.id);
};

const outsidePopupClick = () => {
  emit('outsidePopupClick');
};

const userEmail = computed(() => {
  return LocalStorageUtils.getUserEmail() || '';
});
</script>

<template>
  <div>
    <component :is="link.route.startsWith('http') ? 'a' : link.id === SidebarLinkIdentifiers.USER_PROFILE ? 'div' : 'router-link'"
      :to="{ name: link.route }"
      :class="`sidebar-link ${isLinkActive ? 'router-link-active' : ''}`"
      @click="linkClick"
      ref="reference"
      v-ripple>
      <FontAwesomeIcon :icon="[`${isLinkActive ? 'fas' : link.iconStyle}`, `${link.icon}`]"
        class="link-icon"
        v-if="link.id !== SidebarLinkIdentifiers.USER_PROFILE"/>
      <UserAvatar :avatar-url="link.icon"
        :user-name="link.label"
        :user-email="userEmail"
        v-if="link.id === SidebarLinkIdentifiers.USER_PROFILE"
        :loading="link.loading"
        :size="32"/>
      <div class="link-text"
        :class="{ 'selected-link-text': link.selected }"
        v-if="link.id !== SidebarLinkIdentifiers.USER_PROFILE">
        {{ link.label }}
      </div>
    </component>

    <div v-for="nestedLink in link.nestedLinks" :key="nestedLink.id">
      <component :is="nestedLink.route.startsWith('http') ? 'a' : 'router-link'"
        class="sidebar-link nested-link"
        :to="{ name: nestedLink.route }"
        :href="nestedLink.route"
        v-ripple>
        <FontAwesomeIcon :icon="[
            `${isLinkActive ? 'fas' : nestedLink.iconStyle}`,
            `${nestedLink.icon}`,
          ]"
          class="link-icon nested-link-icon"/>
        <div class="link-text nested-link-text">{{ nestedLink.label }}</div>
      </component>
    </div>

    <Popup :visible="showProfileOptions"
      :x="xValue"
      :y="yValue"
      @outsidePopupClick="outsidePopupClick"
      ref="popupReference">
      <template #popupContent>
        <UserOptions @closePopup="outsidePopupClick" />
      </template>
    </Popup>
  </div>
</template>

<style scoped lang="scss">
.sidebar-link {
  width: 100%;
  display: flex;
  padding: 11px 16px 11px 30px;
  align-items: center;
  text-align: left;
  justify-content: flex-start;
  text-decoration: none;
  color: black;
  cursor: pointer;
  border-top: 1px solid transparent;
  border-bottom: 1px solid transparent;
  &:hover {
    background-color: hsla(0, 0%, 0%, 0.15);
  }
}
.nested-link {
  padding-left: 45px;
}
.link-icon {
  color: hsl(213, 100%, 56%);
  font-size: 20px;
  width: 30px;
  margin-right: 16px;
}
.nested-link-icon {
  font-size: 18px;
  margin-right: 0px;
}
.link-text {
  padding: 0 16px;
  font-size: 16px;
  font-weight: 500;
}
.selected-link-text {
  font-weight: 700;
}
.nested-link-text {
  font-size: 14px;
}
.router-link-active {
  background-color: hsl(0, 0%, 97.3%);
  border-top: 1px solid hsl(0, 0%, 88.2%);
  border-bottom: 1px solid hsl(0, 0%, 88.2%);
}
</style>
